/** @file
 * @brief A custom tooltip component for React Joyride, styled using Material-UI.
 * @details This component is designed to be used as a tooltip in React Joyride tours. It utilizes Material-UI's Dialog component to display the tooltip content, including the title, description, and navigation buttons (Back and Next/Last). The tooltip adapts its size based on the length of the content and supports HTML content through the use of `dangerouslySetInnerHTML`. The component also handles navigation between steps in the tour, allowing users to move back and forth through the steps.
 */

// vendor libraries:
import React from 'react';
import { TooltipRenderProps } from 'react-joyride';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

// our stuff:
import ClosableDialogTitle from './ClosableDialogTitle';

export type MaterialTooltipProps = TooltipRenderProps;

function MaterialTooltip({
    isLastStep,
    index,
    step,
    backProps,
    skipProps,
    primaryProps,
    tooltipProps,
}: Readonly<MaterialTooltipProps>): JSX.Element {
    const hasPreviousStep = index > 0;
    const content = (typeof step.content === "string") ? <div dangerouslySetInnerHTML={{ __html: step.content }} /> : step.content;
    const maxWidth = (typeof step.content === "string") && (step.content.length > 1100 /* ZLP for home page*/) ? "md" :
        (typeof step.content === "string") && (step.content.length > 200) ? "sm" : "xs";
    return <Dialog open {...tooltipProps} PaperProps={{ elevation: 24 }} maxWidth={maxWidth}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        {step.title && <ClosableDialogTitle id="alert-dialog-title" {...skipProps}>{step.title}</ClosableDialogTitle>}
        <DialogContent>
            <DialogContentText component="div" id="alert-dialog-description">
                {content}
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            {hasPreviousStep &&
                <Button {...backProps} >
                    {step.locale.back}
                </Button>
            }
            <Button {...primaryProps} color="primary">
                {isLastStep ? step.locale.last : step.locale.next}
            </Button>
        </DialogActions>
    </Dialog>
}

export default MaterialTooltip;